import React, { useEffect, useRef, useState } from 'react';

/**
 * Reveal — кит компонентов (WEB-2237, сквозное решение «2001-П8»).
 *
 * Одна кривая и одна длительность на ВСЕ раскрывашки кабинета: та же, с которой
 * выезжает шторка (`Drawer`) — решение владельца 17.09.2026 («а скрытие
 * раскрытие настроек должно плавно выезжать… вот как журнал плавно»).
 *
 * Почему через `grid-template-rows: 0fr → 1fr`, а не через `max-height`:
 * высоту содержимого не нужно измерять руками (иначе при переносе строки на
 * другой ширине текст прыгает или обрезается). Образец — компонент `Reveal`
 * в макете `../web2app-spec/design/project-settings-mockup/psettings-app.jsx`.
 *
 * Свёрнутое состояние занимает ровно 0 px, помечено `aria-hidden` и не ловит
 * фокус с клавиатуры (`inert` на внутренней обёртке) — иначе в разделе
 * появляется невидимая пустота и «прыгающий» фокус.
 *
 * Собственного текста у компонента нет — правило двух языков его не касается.
 */

/** Кривая шторки antd (`Drawer`). Менять только вместе со всем кабинетом. */
export const REVEAL_EASING = 'cubic-bezier(0.645, 0.045, 0.355, 1)';
/** Длительность раскрытия, мс. */
export const REVEAL_DURATION_MS = 300;
/** Прозрачность догоняет высоту чуть раньше — так движение читается мягче. */
const OPACITY_RATIO = 0.8;

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

/** Системная настройка «уменьшить движение»: при ней раскрытие мгновенное. */
export function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    if (
      typeof window === 'undefined' ||
      typeof window.matchMedia !== 'function'
    ) {
      return;
    }
    const mq = window.matchMedia(REDUCED_MOTION_QUERY);
    setReduced(mq.matches);
    const onChange = (event: MediaQueryListEvent) => setReduced(event.matches);
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', onChange);
      return () => mq.removeEventListener('change', onChange);
    }
    // Safari < 14 и часть встроенных браузеров знают только старый вид подписки.
    mq.addListener(onChange);
    return () => mq.removeListener(onChange);
  }, []);

  return reduced;
}

export interface RevealProps {
  /** Раскрыто или свёрнуто. */
  open: boolean;
  children: React.ReactNode;
  /** Длительность, мс. По умолчанию — общая для кабинета (300). */
  duration?: number;
  /**
   * Содержимое строится при ПЕРВОМ раскрытии и дальше живёт скрытым.
   *
   * Это способ по умолчанию для всего, внутри чего человек что-то ВВОДИТ:
   * снос тела убивает незасохранённый черновик молча (шрам 15.09 —
   * `payments/kit/PayKit.tsx`, `PayBranch`: набрал потолок списаний, свернул
   * ветку, развернул — поле пустое). Заранее монтировать тоже нельзя: дети
   * ходят в API, и никогда не раскрытые ветки дали бы лишние запросы на
   * каждом входе в раздел.
   */
  lazy?: boolean;
  /**
   * Снимать содержимое с разметки, когда блок свёрнут (тяжёлые списки,
   * журналы, внутри которых НЕТ незасохранённого ввода). Снятие происходит
   * ПОСЛЕ сворачивания, иначе вместо плавного закрытия получится тот же рывок.
   */
  unmountOnClose?: boolean;
  'data-testid'?: string;
}

export function Reveal({
  open,
  children,
  duration = REVEAL_DURATION_MS,
  lazy = false,
  unmountOnClose = false,
  ...rest
}: RevealProps) {
  const reducedMotion = usePrefersReducedMotion();
  const [mounted, setMounted] = useState(open);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!unmountOnClose) {
      // Ленивый режим: раскрыли хоть раз — содержимое дальше живёт скрытым.
      if (lazy && open) {
        setMounted(true);
      }
      return;
    }
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (open) {
      setMounted(true);
      return;
    }
    if (reducedMotion) {
      setMounted(false);
      return;
    }
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      setMounted(false);
    }, duration);
    return () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [open, lazy, unmountOnClose, duration, reducedMotion]);

  const transition = reducedMotion
    ? 'none'
    : `grid-template-rows ${duration}ms ${REVEAL_EASING}, opacity ${Math.round(
        duration * OPACITY_RATIO,
      )}ms ${REVEAL_EASING}`;

  // `inert` пока не описан в типах React 18 — атрибут отдаём через приведение.
  const inertProps = open
    ? {}
    : ({ inert: '' } as unknown as React.HTMLAttributes<HTMLDivElement>);

  return (
    <div
      data-testid={rest['data-testid']}
      aria-hidden={open ? undefined : true}
      style={{
        display: 'grid',
        gridTemplateRows: open ? '1fr' : '0fr',
        opacity: open ? 1 : 0,
        transition,
      }}
    >
      <div style={{ overflow: 'hidden', minHeight: 0 }} {...inertProps}>
        {(lazy || unmountOnClose) && !mounted && !open ? null : children}
      </div>
    </div>
  );
}
